import { useApi } from 'Shared/services/Api';
import {
  DiffCategory,
  DiffData,
  DiffItemData,
  PolicyDiff,
  PolicyDiffResponse,
  PolicyDiffVehicleCoveragesAndEndorsements,
  VersionedObjectDiff,
  VehicleGroup,
} from './diffTypes';

const categoryLabels: Record<string, string> = {
  insurableItemParties: 'Drivers',
  insurableItems: 'Vehicles',
  policyCoverages: 'Policy Coverages',
  policyEndorsements: 'Policy Endorsements',
  vehicleCoveragesAndEndorsements: 'Vehicle Coverages & Endorsements',
  fullTermWrittenPremium: 'Full Term Premium',
  multiLineDiscounts: 'Multi-Line Discounts',
  addresses: 'Addresses',
  insurableItemRelationships: 'Driver Assignments',
};

const categoryOrder = [
  'insurableItemParties',
  'insurableItems',
  'policyCoverages',
  'policyEndorsements',
  'vehicleCoveragesAndEndorsements',
  'insurableItemRelationships',
  'fullTermWrittenPremium',
  'multiLineDiscounts',
  'addresses',
];

function stripVersionFields(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(stripVersionFields);
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (key === 'effectivePeriod' || key === 'versionInfo') continue;
      result[key] = stripVersionFields(value);
    }
    return result;
  }
  return obj;
}

function getStringField(
  source: Record<string, unknown> | undefined,
  keys: string[]
): string | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

function generateSummary(
  category: string,
  item: Record<string, unknown>
): string {
  switch (category) {
    case 'insurableItemParties': {
      const person = (item.person || {}) as Record<string, unknown>;
      return `${person.firstName || ''} ${person.lastName || ''}`.trim();
    }
    case 'insurableItems': {
      const identification = ((
        (item.vehicle as Record<string, unknown>)?.detail as Record<
          string,
          unknown
        >
      )?.identification || {}) as Record<string, unknown>;
      return `${identification.year || ''} ${
        identification.make || ''
      } ${identification.model || ''}`.trim();
    }
    case 'policyCoverages':
    case 'policyEndorsements': {
      const nested = item.coverage as Record<string, unknown> | undefined;
      const fields = [
        'coverageLabel',
        'coverageName',
        'label',
        'displayName',
        'name',
        'coverageType',
        'type',
      ];
      return String(
        getStringField(item, fields) || getStringField(nested, fields) || ''
      );
    }
    case 'addresses': {
      return `${item.street || ''}, ${item.city || ''} ${
        item.state || ''
      }`.trim();
    }
    case 'insurableItemRelationships': {
      return `${item.from || ''} \u2192 ${item.to || ''}`.trim();
    }
    case 'multiLineDiscounts': {
      return String(item.discountName || item.name || '');
    }
    default:
      return JSON.stringify(item).substring(0, 50);
  }
}

function processDiffItem(
  diff: VersionedObjectDiff,
  category: string
): DiffItemData {
  const old = diff.old
    ? (stripVersionFields(diff.old) as Record<string, unknown>)
    : undefined;
  const new_ = diff.new
    ? (stripVersionFields(diff.new) as Record<string, unknown>)
    : undefined;
  const summary = generateSummary(category, new_ || old || {});
  return {
    type: diff.type,
    summary,
    ...(old && { old }),
    ...(new_ && { new: new_ }),
  };
}

function processVehicleGroups(
  vehicleData: PolicyDiffVehicleCoveragesAndEndorsements[]
): VehicleGroup[] {
  return vehicleData.map((vehicle) => {
    const m = vehicle.metadata || {};
    const vin = m.vin ? ` (VIN: ${m.vin})` : '';
    const vehicleLabel = `${m.year || ''} ${m.make || ''} ${
      m.model || ''
    }${vin}`.trim();
    return {
      vehicleId: vehicle.insurableItemIdentifier,
      vehicleLabel,
      coverages: vehicle.coverages.map((cov) =>
        processDiffItem(cov, 'policyCoverages')
      ),
      endorsements: vehicle.endorsements.map((end) =>
        processDiffItem(end, 'policyEndorsements')
      ),
    };
  });
}

function processDiff(rawDiff: PolicyDiff): DiffData {
  const categories: DiffCategory[] = [];
  let fullTermWrittenPremium: DiffData['fullTermWrittenPremium'];

  const addCategory = (key: string, items: DiffItemData[]) => {
    if (items.length > 0) {
      categories.push({ key, label: categoryLabels[key] || key, items });
    }
  };

  addCategory(
    'insurableItemParties',
    rawDiff.insurableItemParties.map((i) =>
      processDiffItem(i, 'insurableItemParties')
    )
  );
  addCategory(
    'insurableItems',
    rawDiff.insurableItems.map((i) => processDiffItem(i, 'insurableItems'))
  );
  addCategory(
    'policyCoverages',
    rawDiff.policyCoverages.map((i) => processDiffItem(i, 'policyCoverages'))
  );
  addCategory(
    'policyEndorsements',
    rawDiff.policyEndorsements.map((i) =>
      processDiffItem(i, 'policyEndorsements')
    )
  );

  if (rawDiff.vehicleCoveragesAndEndorsements.length > 0) {
    const vehicleGroups = processVehicleGroups(
      rawDiff.vehicleCoveragesAndEndorsements
    );
    if (vehicleGroups.length > 0) {
      categories.push({
        key: 'vehicleCoveragesAndEndorsements',
        label: categoryLabels['vehicleCoveragesAndEndorsements'],
        vehicleGroups,
      });
    }
  }

  addCategory(
    'insurableItemRelationships',
    rawDiff.insurableItemRelationships.map((i) =>
      processDiffItem(i, 'insurableItemRelationships')
    )
  );

  if (rawDiff.fullTermWrittenPremium) {
    const oldAmount = rawDiff.fullTermWrittenPremium.old?.amount?.value || 0;
    const newAmount = rawDiff.fullTermWrittenPremium.new?.amount?.value || 0;
    fullTermWrittenPremium = {
      oldValue: oldAmount,
      newValue: newAmount,
      delta: newAmount - oldAmount,
      effectiveDate: rawDiff.fullTermWrittenPremium.new?.effectivePeriod
        ?.startAt as string | undefined,
    };
  }

  addCategory(
    'multiLineDiscounts',
    rawDiff.multiLineDiscounts.map((i) =>
      processDiffItem(i, 'multiLineDiscounts')
    )
  );
  addCategory(
    'addresses',
    rawDiff.addresses.map((i) => processDiffItem(i, 'addresses'))
  );

  categories.sort(
    (a, b) => categoryOrder.indexOf(a.key) - categoryOrder.indexOf(b.key)
  );

  return { categories, fullTermWrittenPremium };
}

export function useDiffData(
  policyId: string,
  id: string,
  source: 'version' | 'draft'
) {
  const url =
    source === 'draft'
      ? `/policies/${policyId}/drafts/${id}/raw-diff`
      : `/policies/${policyId}/versions/${id}/raw-diff?requirePreviousVersion=false`;

  const [{ data: raw, loading, error }] = useApi<PolicyDiffResponse>(
    url,
    false
  );
  const data = raw ? processDiff(raw.diff) : undefined;

  return { data, loading, error };
}
